import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, EMPTY, map, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DragDropModule, CdkDragEnd } from '@angular/cdk/drag-drop';

import { ChatMessage, ChatService } from '@shared/services/chat.service';
import { SKUService } from '@app/feature/center-business/goods/sku/services/sku.service';
import { SKUSearchPayload } from '@app/feature/center-business/goods/sku/models/sku.interface';
import { SpuApiService } from '@app/feature/center-business/goods/spu/services/spu-api.service';
import { ChatFormatPipe } from './chat-format.pipe';
import { groupConversations } from './chat-history';
import {
  BRAND_SCREEN_URL,
  brandChatSuggestions,
  ChatSuggestion,
  contextChatSuggestions,
  DEFAULT_CHAT_SUGGESTIONS,
  followUpSuggestions,
  MAX_REPLY_SUGGESTIONS,
  MAX_SUGGESTIONS,
  PRICE_SCREEN_SUGGESTIONS,
  PRICE_SCREEN_URL,
  SKU_SCREEN_URL,
  skuChatSuggestions,
  SPU_SCREEN_URL,
  spuChatSuggestions,
  STOCK_SCREEN_SUGGESTIONS,
  STOCK_SCREEN_URL,
  uniqueSuggestions,
  withPrefill,
} from './chat-suggestions';
import { BrandService } from '@app/feature/center-business/goods/brand/services/brand.service';

const DRAG_CLICK_THRESHOLD_PX = 6;
const PANEL_HEIGHT_PX = 520;
const PANEL_GAP_PX = 12;

@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, DragDropModule, ToastModule, ChatFormatPipe],
  providers: [MessageService],
  templateUrl: './chat-widget.component.html',
  styleUrl: './chat-widget.component.css',
})
export class ChatWidgetComponent {
  protected readonly chat = inject(ChatService);
  private readonly skuService = inject(SKUService);
  private readonly spuApiService = inject(SpuApiService);
  private readonly brandService = inject(BrandService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);

  protected readonly draft = signal('');
  protected readonly suggestions = signal<ChatSuggestion[]>(DEFAULT_CHAT_SUGGESTIONS);
  /** Only on an empty conversation; after that the chips come under each reply. */
  protected readonly showSuggestions = computed(() => !this.chat.historyLoading() && !this.chat.hasMessages());
  /** Under the latest reply: up to 3 chips about it (AI Agents' follow-ups, "similar"/"compare"),
   * then the fixed chips - both show right away. */
  protected readonly followUps = computed<ChatSuggestion[]>(() => {
    const last = this.chat.messages().at(-1);
    if (!last || last.role !== 'assistant' || this.chat.sending()) {
      return [];
    }
    return uniqueSuggestions([...this.replySuggestions().slice(0, MAX_REPLY_SUGGESTIONS), ...this.suggestions()]).slice(0, MAX_SUGGESTIONS);
  });
  /** About the latest reply: AI Agents' follow-ups plus "similar"/"compare" on its products. */
  private readonly replySuggestions = computed<ChatSuggestion[]>(() => {
    const last = this.chat.messages().filter((message) => message.role === 'assistant').at(-1);
    if (!last) {
      return [];
    }
    const cards = last.productGroups?.length
      ? last.productGroups.flatMap((group) => group.products)
      : (last.products ?? []);
    return uniqueSuggestions([...followUpSuggestions(last.followUps ?? []), ...contextChatSuggestions(cards)]);
  });
  /** The history list replaces the chat view inside the same panel. */
  protected readonly historyOpen = signal(false);
  protected readonly conversationGroups = computed(() => groupConversations(this.chat.conversations()));
  /** Set when the employee reopened an older conversation from the list - its cards show the
   * price/stock of back then, so the panel says so. */
  protected readonly openedFromHistory = signal(false);
  private readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('inputEl');
  protected readonly iconPosition = signal({ x: 0, y: 0 });
  protected readonly panelStyle = signal<{ top: string; transformOrigin: string }>({
    top: 'auto',
    transformOrigin: 'bottom right',
  });
  private readonly iconEl = viewChild<ElementRef<HTMLElement>>('iconEl');
  private readonly messagesEl = viewChild<ElementRef<HTMLElement>>('messagesEl');
  private readonly textEls = viewChildren<ElementRef<HTMLElement>>('textEl');
  protected readonly wrappedMessageIndexes = signal<ReadonlySet<number>>(new Set());
  private suppressAutoScrollOnce = false;
  private justDragged = false;

  constructor() {
    effect(() => {
      this.chat.messages();
      this.chat.open();
      if (this.suppressAutoScrollOnce) {
        this.suppressAutoScrollOnce = false;
        return;
      }
      setTimeout(() => {
        const el = this.messagesEl()?.nativeElement;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    });

    effect(() => {
      this.chat.messages();
      this.chat.historyLoading();
      setTimeout(() => {
        const wrapped = new Set<number>();
        const range = document.createRange();
        this.textEls().forEach((el, i) => {
          range.selectNodeContents(el.nativeElement);
          if (range.getClientRects().length > 1) {
            wrapped.add(i);
          }
        });
        this.wrappedMessageIndexes.set(wrapped);
        if (this.chat.open()) {
          this.updatePanelPosition();
        }
      });
    });

    const destroyRef = inject(DestroyRef);
    this.router.events
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => this.chat.close());

    // Any navigation closes the panel (above), so the page can't change while it's open - resolving
    // chips on open avoids an extra SKU lookup on every SKU screen visit when chat is never used.
    toObservable(this.chat.open)
      .pipe(
        tap(() => {
          this.historyOpen.set(false);
        }),
        switchMap((open) => (open ? this.suggestionsForCurrentPage() : EMPTY)),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((suggestions) => this.suggestions.set(suggestions));
  }

  /** On a SKU/SPU/brand view or edit screen the chips are built from what is open there (SKU name
   * and price, SPU name, brand name); stock, purchase and price screens get their own fixed set.
   * The default set shows while a lookup loads and stays if it fails. */
  private suggestionsForCurrentPage(): Observable<ChatSuggestion[]> {
    const skuId = SKU_SCREEN_URL.exec(this.router.url)?.[1];
    const spuId = SPU_SCREEN_URL.exec(this.router.url)?.[1];
    const brandId = BRAND_SCREEN_URL.exec(this.router.url)?.[1];
    let suggestions$: Observable<ChatSuggestion[]>;
    if (skuId) {
      suggestions$ = this.skuService.getSkuDetailById(skuId).pipe(
        map((sku) => {
          const name = sku.spuName || sku.skuName;
          return name ? skuChatSuggestions(name, sku.salesPrice) : DEFAULT_CHAT_SUGGESTIONS;
        }),
      );
    } else if (spuId) {
      suggestions$ = this.spuApiService
        .getSpuById(Number(spuId))
        .pipe(map((spu) => (spu?.name ? spuChatSuggestions(spu.name) : DEFAULT_CHAT_SUGGESTIONS)));
    } else if (brandId) {
      suggestions$ = this.brandService
        .getBrandById(decodeURIComponent(brandId))
        .pipe(map((brand) => (brand?.brandName ? brandChatSuggestions(brand.brandName) : DEFAULT_CHAT_SUGGESTIONS)));
    } else if (STOCK_SCREEN_URL.test(this.router.url)) {
      return of(STOCK_SCREEN_SUGGESTIONS);
    } else if (PRICE_SCREEN_URL.test(this.router.url)) {
      return of(PRICE_SCREEN_SUGGESTIONS);
    } else {
      return of(DEFAULT_CHAT_SUGGESTIONS);
    }
    return suggestions$.pipe(
      catchError(() => of(DEFAULT_CHAT_SUGGESTIONS)),
      startWith(DEFAULT_CHAT_SUGGESTIONS),
    );
  }

  protected onIconDragEnded(event: CdkDragEnd): void {
    const distance = event.distance;
    const moved = Math.hypot(distance.x, distance.y) > DRAG_CLICK_THRESHOLD_PX;
    if (moved) {
      this.iconPosition.set(event.source.getFreeDragPosition());
      this.justDragged = true;
      setTimeout(() => {
        this.justDragged = false;
      }, 300);
    }
  }

  protected onIconClick(): void {
    if (this.justDragged) {
      this.justDragged = false;
      return;
    }
    if (!this.chat.open()) {
      this.updatePanelPosition();
    }
    this.chat.toggle();
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (this.chat.open()) {
      this.updatePanelPosition();
    }
  }

  private updatePanelPosition(): void {
    const iconRect = this.iconEl()?.nativeElement.getBoundingClientRect();
    if (!iconRect) {
      return;
    }
    const viewportHeight = document.documentElement.clientHeight;
    const fitsAbove = iconRect.top - PANEL_GAP_PX - PANEL_HEIGHT_PX >= 0;
    const rawTop = fitsAbove
      ? iconRect.top - PANEL_GAP_PX - PANEL_HEIGHT_PX
      : iconRect.bottom + PANEL_GAP_PX;
    const maxTop = viewportHeight - PANEL_HEIGHT_PX - PANEL_GAP_PX;
    const top = Math.min(Math.max(rawTop, PANEL_GAP_PX), Math.max(PANEL_GAP_PX, maxTop));
    this.panelStyle.set({ top: `${top}px`, transformOrigin: fitsAbove ? 'bottom right' : 'top right' });
  }

  protected onMessagesScroll(el: HTMLElement): void {
    if (el.scrollTop > 80 || !this.chat.hasMoreHistory() || this.chat.loadingOlder()) {
      return;
    }
    const heightBeforePrepend = el.scrollHeight;
    this.suppressAutoScrollOnce = true;
    this.chat.loadOlder().subscribe(() => {
      setTimeout(() => {
        el.scrollTop += el.scrollHeight - heightBeforePrepend;
      });
    });
  }

  protected isWideMessage(message: ChatMessage, index: number): boolean {
    return (message.products?.length ?? 0) > 0 || this.wrappedMessageIndexes().has(index);
  }

  protected submit(event: Event): void {
    event.preventDefault();
    const text = this.draft();
    this.draft.set('');
    this.chat.send(text);
  }

  protected useSuggestion(suggestion: ChatSuggestion): void {
    const text = this.translateService.instant(suggestion.key, suggestion.params);
    if (suggestion.action === 'send') {
      this.chat.send(text, suggestion.intent);
      return;
    }
    // Keeps what the employee typed. Only fixed prefill chips are command prefixes to swap out - a
    // parameterised one (e.g. a category name) is part of the question, so it stays.
    const prefixes = [...this.suggestions(), ...this.followUps()]
      .filter((chip) => chip.action === 'prefill' && !chip.params)
      .map((chip) => this.translateService.instant(chip.key));
    const value = withPrefill(this.draft(), text, [text, ...prefixes]);
    this.draft.set(value);
    setTimeout(() => {
      const input = this.inputEl()?.nativeElement;
      if (input) {
        input.focus();
        input.setSelectionRange(value.length, value.length);
      }
    });
  }

  protected startNewConversation(): void {
    this.chat.startNewConversation();
    this.draft.set('');
    this.openedFromHistory.set(false);
  }

  protected openHistory(): void {
    this.historyOpen.set(true);
    this.chat.loadConversations();
  }

  protected selectConversation(conversationId: string): void {
    this.historyOpen.set(false);
    if (conversationId === this.chat.conversationId() || this.chat.busy()) {
      return;
    }
    this.chat.openConversation(conversationId);
    this.draft.set('');
    this.openedFromHistory.set(true);
  }

  protected retryConversations(): void {
    this.chat.loadConversations(this.chat.conversations().length > 0);
  }

  /**
   * The chat card only carries skuCode (the AI Agents reply contract) but oms-app's SKU routes need a
   * numeric id + type ('ordinary'/'package') - resolves that via the same search endpoint the SKU
   * list screen already uses, matching on an exact skuCode, before navigating.
   */
  protected openProduct(skuCode: string): void {
    this.navigateToSku(skuCode, 'view');
  }

  /** Separate action on the card (not the same click target as openProduct) so a chat card can
   * jump straight into edit mode without first opening the read-only view. */
  protected editProduct(skuCode: string, event: Event): void {
    event.stopPropagation();
    this.navigateToSku(skuCode, 'edit');
  }

  private navigateToSku(skuCode: string, mode: 'view' | 'edit'): void {
    const payload: SKUSearchPayload = {
      spuCode: null,
      skuCode,
      spuName: null,
      categoryIdList: [],
      status: [],
      setFlag: null,
      page: 0,
      size: 5,
    };
    this.skuService.searchSkuItemsPaginated(payload).subscribe({
      next: (response) => {
        const match = response.content.find((item) => item.skuCode === skuCode);
        if (!match || match.skuType === 'draft') {
          this.showNotFound();
          return;
        }
        this.chat.close();
        this.router.navigate(['/goods/sku', match.skuType, mode, match.id]);
      },
      error: () => this.showNotFound(),
    });
  }

  private showNotFound(): void {
    this.messageService.add({
      key: 'chat-widget',
      severity: 'warn',
      summary: this.translateService.instant('common.messages.warning'),
      detail: this.translateService.instant('chat_widget.productNotFound'),
    });
  }
}
